import { type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import type { ActivityLog, Expense } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { parseMeta, type ActivityLine } from "@/lib/activity";

// Shared pieces for "what happened" (Sept 2026):
//   ActivityRow     — one line in Home's "What changed" and the Activity page
//   ExpenseStory    — in an expense's detail: Spent on / Added (locked) /
//                     Deleted, Restore, and a before/after table per edit
//   ExpenseHistory  — a group's or friend's list: live expenses (rendered by
//                     the page), deleted ones kept in place with Restore, and
//                     edits / restores / clears as their own entries.

// ─── Activity row ──────────────────────────────────────────────────────────

export function ActivityRow({ line, when, initial, color, onClick }: {
  line: ActivityLine; when: string; initial: string; color: string; onClick?: () => void;
}) {
  const body = (
    <>
      <span
        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-serif text-[15px] shrink-0 mt-0.5"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      >
        {initial}
      </span>
      <span className="flex-1 min-w-0 text-sm leading-snug text-foreground">
        <span className="font-semibold">{line.actor}</span> {line.verb}
        {line.subject && <> {line.struck ? <s className="text-muted-foreground">{line.subject}</s> : line.subject}</>}
        {line.where && <> {line.where}</>}
        {line.detail && <span className="block font-mono text-xs text-foreground/80 mt-0.5">{line.detail}</span>}
        <span className="block text-xs text-muted-foreground mt-0.5">{when}</span>
      </span>
      {line.amount && <span className={`font-mono text-[13px] shrink-0 ${line.struck ? "line-through text-muted-foreground" : "text-foreground"}`}>{line.amount}</span>}
    </>
  );
  return onClick ? (
    <button onClick={onClick} className="w-full flex items-start gap-3 py-2.5 text-left">{body}</button>
  ) : (
    <div className="flex items-start gap-3 py-2.5">{body}</div>
  );
}

// ─── The story of one expense (shown in its detail) ─────────────────────

const fullDate = (v: string) => new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const dateTime = (v: string) =>
  `${fullDate(v)}, ${new Date(v).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase()}`;
const shortDay = (v: unknown) => (typeof v === "string" ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

type HistoryResponse = { expenses: Expense[]; activity: ActivityLog[] };

export function historyKey(source: { groupId: string } | { friendId: string }) {
  return "groupId" in source ? ["/api/groups", source.groupId, "history"] : ["/api/friends", source.friendId, "history"];
}

/**
 * Spent on / Added (locked) / Deleted, Restore for deleted ones, and a
 * "What changed" before/after table per edit. Reads the same cached history
 * the list uses, so opening a detail doesn't refetch.
 */
export function ExpenseStory({ expense, source, meId, isAdmin, nameOf, onRestored }: {
  expense: Expense;
  source: { groupId: string } | { friendId: string };
  meId: string;
  isAdmin?: boolean;
  nameOf: (id: string) => string;
  onRestored: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const key = historyKey(source);
  const { data } = useQuery<HistoryResponse>({
    queryKey: key,
    queryFn: async () => (await apiRequest("GET", "groupId" in source ? `/api/groups/${source.groupId}/history` : `/api/friends/${source.friendId}/history`)).json(),
  });
  const who = (id: string | null | undefined) => (!id ? "someone" : id === meId ? "you" : nameOf(id).split(" ")[0]);
  const edits = (data?.activity ?? []).filter((r) => r.expenseId === expense.id && r.action === "expense_edited");
  const canRestore = !!expense.deletedAt && (expense.addedById === meId || expense.deletedById === meId || !!isAdmin);

  const restore = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/expenses/${expense.id}/restore`)).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["/api/activity"] });
      toast({ title: "Expense restored" });
      onRestored();
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error || msg; } catch {}
      toast({ title: "Couldn't restore", description: msg, variant: "destructive" });
    },
  });

  const FIELD: Record<string, { label: string; show: (v: unknown) => string }> = {
    amount: { label: "Amount", show: (v) => (typeof v === "number" ? `$${v.toFixed(2)}` : "") },
    description: { label: "What for", show: (v) => String(v ?? "") },
    date: { label: "Spent on", show: shortDay },
    paidById: { label: "Paid by", show: (v) => { const w = who(String(v)); return w.charAt(0).toUpperCase() + w.slice(1); } },
    notes: { label: "Note", show: (v) => (v ? String(v) : "—") },
  };

  return (
    <div className="space-y-3" data-testid="expense-story">
      <div className="rounded-lg bg-muted/50 p-3 space-y-1.5 text-sm" data-testid="expense-dates">
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">Spent on</span>
          <span className="font-medium text-right">{fullDate(expense.date)}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground flex items-center gap-1">Added <Lock className="w-3 h-3" aria-label="Set automatically, can't be changed" /></span>
          <span className="text-right">{expense.createdAt ? dateTime(expense.createdAt) : "Before Sept 2026"} · by {who(expense.addedById)}</span>
        </div>
        {expense.deletedAt && (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Deleted</span>
            <span className="text-right">{dateTime(expense.deletedAt)} · by {who(expense.deletedById)}</span>
          </div>
        )}
      </div>

      {canRestore && (
        <button
          onClick={() => restore.mutate()}
          disabled={restore.isPending}
          className="w-full rounded-full bg-foreground text-background text-sm font-medium py-3 disabled:opacity-50"
          data-testid="restore-expense"
        >
          {restore.isPending ? "Restoring…" : "Restore expense"}
        </button>
      )}

      {edits.map((row) => {
        const changes = parseMeta(row).changes ?? {};
        const fields = Object.keys(FIELD).filter((f) => changes[f]);
        if (fields.length === 0) return null;
        return (
          <div key={row.id} className="rounded-lg bg-muted/50 p-3" data-testid="what-changed-table">
            <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground mb-1.5">What changed</p>
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="text-[11px] text-muted-foreground">
                  <th className="w-[30%] text-left font-normal pb-1"><span className="sr-only">Field</span></th>
                  <th className="w-[35%] text-left font-normal pb-1">Before</th>
                  <th className="w-[35%] text-left font-normal pb-1">After</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f} className="border-t border-border/60 align-top">
                    <td className="py-1.5 pr-2 text-muted-foreground">{FIELD[f].label}</td>
                    <td className="py-1.5 pr-2 line-through text-muted-foreground break-words">{FIELD[f].show(changes[f].from)}</td>
                    <td className="py-1.5 break-words">{FIELD[f].show(changes[f].to)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-1.5">Edited by {who(row.userId)} · {dateTime(row.createdAt)}</p>
          </div>
        );
      })}
    </div>
  );
}

export function EditedTag() {
  return <span className="ml-1.5 inline-block align-[2px] rounded-full bg-accent text-accent-foreground px-1.5 py-px text-[10.5px] font-medium">Edited</span>;
}

export function DeletedTag() {
  return <span className="ml-1.5 inline-block align-[2px] rounded-full bg-muted text-muted-foreground px-1.5 py-px text-[10.5px] font-medium no-underline">Deleted</span>;
}

// ─── Group / friend history ────────────────────────────────────────────────

type Item =
  | { kind: "live"; key: string; at: string; expense: Expense }
  | { kind: "deleted"; key: string; at: string; expense: Expense }
  | { kind: "change"; key: string; at: string; row: ActivityLog };

// Change-log actions that get their own entry. Adds, settle-ups and deletes
// aren't repeated here — the expense row itself (live or struck) shows them.
// Edits and restores live on the expense itself (Edited tag + its detail).
const OWN_ENTRY = new Set(["expenses_cleared", "expenses_imported", "member_joined", "member_added"]);

export function ExpenseHistory({
  source, liveExpenses, meId, nameOf, renderLive, onOpenDeleted, emptyState,
}: {
  source: { groupId: string } | { friendId: string };
  liveExpenses: Expense[];
  meId: string;
  nameOf: (id: string) => string;
  renderLive: (expense: Expense) => ReactNode;
  /** Tapping a deleted row opens its detail (who deleted it, Restore). */
  onOpenDeleted: (expense: Expense) => void;
  emptyState: ReactNode;
}) {
  const url = "groupId" in source ? `/api/groups/${source.groupId}/history` : `/api/friends/${source.friendId}/history`;
  const { data } = useQuery<HistoryResponse>({
    queryKey: historyKey(source),
    queryFn: async () => (await apiRequest("GET", url)).json(),
  });

  const items: Item[] = [
    ...liveExpenses.map((e): Item => ({ kind: "live", key: `l-${e.id}`, at: e.date, expense: e })),
    ...(data?.expenses ?? []).filter((e) => e.deletedAt).map((e): Item => ({ kind: "deleted", key: `d-${e.id}`, at: e.date, expense: e })),
    ...(data?.activity ?? []).filter((r) => OWN_ENTRY.has(r.action)).map((r): Item => ({ kind: "change", key: `a-${r.id}`, at: r.createdAt, row: r })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  if (items.length === 0) return <>{emptyState}</>;

  const months: { label: string; items: Item[] }[] = [];
  for (const it of items) {
    const label = new Date(it.at).toLocaleDateString(undefined, { month: "long", year: "numeric" });
    const last = months[months.length - 1];
    if (last && last.label === label) last.items.push(it);
    else months.push({ label, items: [it] });
  }
  const first = (id: string) => (id === meId ? "You" : nameOf(id).split(" ")[0]);

  return (
    <div data-testid="expense-history">
      {months.map((m) => (
        <div key={m.label}>
          <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-4 mb-1">{m.label}</h3>
          {m.items.map((it) => {
            if (it.kind === "live") return <div key={it.key}>{renderLive(it.expense)}</div>;
            if (it.kind === "deleted") {
              const e = it.expense;
              const d = new Date(e.date);
              return (
                <button
                  key={it.key}
                  onClick={() => onOpenDeleted(e)}
                  className="w-full flex items-center gap-4 py-4 border-b border-border text-left"
                  data-testid={`deleted-expense-${e.id}`}
                >
                  <div className="w-11 text-center shrink-0 opacity-50">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{d.toLocaleDateString(undefined, { month: "short" })}</div>
                    <div className="font-serif text-2xl leading-none">{d.getDate()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium tracking-tight truncate text-muted-foreground">
                      <span className="line-through">{e.isSettlement ? "Settlement" : e.description}</span>
                      <DeletedTag />
                    </p>
                    <p className="font-mono text-xs text-muted-foreground truncate mt-0.5">
                      {e.isSettlement
                        ? `${first(e.paidById)} paid ${first(e.splitAmongIds[0] ?? "")}`
                        : `${first(e.paidById)} paid · split ${e.splitAmongIds.length} ways`}
                    </p>
                  </div>
                  <span className="font-mono text-sm line-through text-muted-foreground shrink-0">${e.amount.toFixed(2)}</span>
                </button>
              );
            }
            // Group-level events: a quiet one-line note, like a chat system message.
            const row = it.row;
            return (
              <p key={it.key} className="text-center text-xs text-muted-foreground py-3" data-testid={`history-note-${row.id}`}>
                {row.userId === meId ? "You" : row.userName.split(" ")[0]} {row.description} · {new Date(row.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </p>
            );
          })}
        </div>
      ))}
    </div>
  );
}
